import { memo, type FC, useCallback, useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useDebounce } from "@/hooks/useDebounce";

interface CompaniesExploreSearchInputProps {
  initialValue: string;
  onDebouncedChange: (value: string) => void;
  debounceMs?: number;
}

export const CompaniesExploreSearchInput: FC<CompaniesExploreSearchInputProps> =
  memo(function CompaniesExploreSearchInput({
    initialValue,
    onDebouncedChange,
    debounceMs = 600,
  }) {
    const [value, setValue] = useState(initialValue);
    const debouncedValue = useDebounce(value, debounceMs);
    const lastEmittedRef = useRef(initialValue);

    useEffect(() => {
      setValue(initialValue);
      lastEmittedRef.current = initialValue;
    }, [initialValue]);

    useEffect(() => {
      if (debouncedValue === lastEmittedRef.current) return;
      lastEmittedRef.current = debouncedValue;
      onDebouncedChange(debouncedValue);
    }, [debouncedValue, onDebouncedChange]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
      },
      []
    );

    const handleClear = useCallback(() => {
      setValue("");
    }, []);

    return (
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search for name, URL, or hgId"
        value={value}
        onChange={handleChange}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        sx={{
          maxWidth: 800,
        }}
        slotProps={{
          htmlInput: {
            "data-testid": "companies-search-input",
          },
          input: {
            sx: {
              "& .MuiOutlinedInput-input": {
                paddingTop: "12px",
                paddingBottom: "12px",
              },
            },
            endAdornment: (
              <InputAdornment position="end">
                {value.trim() ? (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={handleClear}
                    sx={{ color: "action.active" }}
                  >
                    <ClearIcon />
                  </IconButton>
                ) : (
                  <SearchIcon sx={{ color: "action.active" }} />
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    );
  });
